import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
  Observable,
  catchError,
  of,
  retry,
  timer,
  throwError,
  from,
  switchMap,
} from 'rxjs';
import { Router } from '@angular/router';

/** 3rd Party Packages */
import { TranslateService } from '@ngx-translate/core';
import { KeycloakService } from 'keycloak-angular';

/** Services */
import { AlertService } from 'src/app/core/services/alert.service';
import { AuthService } from '../services/auth.service';

let isRefreshing = false;

export const errorHandlerInterceptor: HttpInterceptorFn = (req, next) => {
  const alertService = inject(AlertService);
  const translate = inject(TranslateService);
  const authService = inject(AuthService);
  const keycloakService = inject(KeycloakService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error) => {
      if (error.status === 401 && !isRefreshing) {
        return handle401Error(
          req,
          next,
          keycloakService,
          router,
          alertService,
          translate,
        );
      }
      return handleError(error);
    }),
  );

  function shouldRetry(error: HttpErrorResponse) {
    if (error.status === 500) return timer(500);

    throw error;
  }

  function handleError(error: HttpErrorResponse): Observable<HttpEvent<any>> {
    const status = error.status;
    let errorMessage =
      error?.error?.detail ||
      error?.error?.message ||
      error?.error?.error ||
      error.message;

    if (error?.error?.errors) {
      const validationErrors = error.error.errors;
      errorMessage = Object.keys(validationErrors)
        .map((key) => {
          const field = key.split('.').pop();
          const messages = validationErrors[key].join('\n');
          return `${field}: ${messages}`;
        })
        .join('\n');
    }

    if (status === 401) {
      // Token refresh is handled separately, but show message if it fails
      alertService.alert({
        type: 'error',
        message: translate.instant(
          'your_session_has_expired_please_log_in_again.capitalize',
        ),
      });
    } else if (status === 400) {
      alertService.alert({
        type: 'error',
        message:
          errorMessage ||
          translate.instant(
            'sorry_your_request_cannot_be_processed_please_provide_all_required_fields_and_try_again.capitalize',
          ),
      });
    } else if (status === 403) {
      alertService.alert({
        type: 'error',
        message: translate.instant(
          'you_are_not_authorized_for_this_request.capitalize',
        ),
      });
    } else if (status === 413) {
      alertService.alert({
        type: 'error',
        message: translate.instant(
          'the_data_you_are_trying_to_send_exceeds_the_servers_maximum_size_limit_please_reduce_the_payload_size.capitalize',
        ),
      });
    } else if (status === 422) {
      alertService.alert({
        type: 'error',
        message:
          errorMessage || translate.instant('invalid_request.capitalize'),
      });
    } else if (status === 404) {
      alertService.alert({
        type: 'error',
        message:
          errorMessage ||
          translate.instant('resource_does_not_exist.capitalize'),
      });
    } else if (status === 409) {
      alertService.alert({
        type: 'error',
        message:
          errorMessage ||
          translate.instant(
            'a_user_with_this_email_already_exists_please_use_a_different_email.capitalize',
          ),
      });
    } else if (status === 500) {
      alertService.alert({
        type: 'error',
        message:
          errorMessage ||
          translate.instant(
            'something_went_wrong_please_try_again_later.capitalize',
          ),
      });
    } else {
      alertService.alert({
        type: 'error',
        message: translate.instant(
          'something_went_wrong_please_try_again_later.capitalize',
        ),
      });
    }

    throw error;
  }

  function handle401Error(
    req: any,
    next: any,
    keycloakService: KeycloakService,
    router: Router,
    alertService: AlertService,
    translate: TranslateService,
  ): Observable<HttpEvent<any>> {
    // Check if we're on a public portal page - don't auto-login for these
    const currentUrl = router.url;
    const isPublicPortalPage =
      currentUrl === '/' ||
      currentUrl.startsWith('/verifications') ||
      currentUrl.startsWith('/services') ||
      currentUrl.startsWith('/announcements') ||
      currentUrl.startsWith('/news-events') ||
      currentUrl.startsWith('/occupational-assessment');

    if (isPublicPortalPage) {
      // Don't trigger login for public pages, just return the error
      return throwError(() => new Error('Unauthorized'));
    }

    if (!isRefreshing) {
      isRefreshing = true;

      return from(keycloakService.updateToken(30)).pipe(
        switchMap((refreshed): Observable<HttpEvent<any>> => {
          isRefreshing = false;
          if (refreshed) {
            // Token refreshed successfully, retry the original request
            return next(req) as Observable<HttpEvent<any>>;
          } else {
            // Token refresh failed, redirect to login
            alertService.alert({
              type: 'error',
              message: translate.instant(
                'your_session_has_expired_please_log_in_again.capitalize',
              ),
            });
            keycloakService.login();
            return throwError(() => new Error('Token refresh failed'));
          }
        }),
        catchError((error): Observable<HttpEvent<any>> => {
          isRefreshing = false;
          // Token refresh failed, redirect to login
          alertService.alert({
            type: 'error',
            message: translate.instant(
              'your_session_has_expired_please_log_in_again.capitalize',
            ),
          });
          keycloakService.login();
          return throwError(() => error);
        }),
      );
    }

    return throwError(() => new Error('Already refreshing token'));
  }
};
